import { useQuery } from '@tanstack/react-query';
import type { Delivery, RequestDetail, RequestSummary } from '@syndiqia/comms/contracts';
import type { Role } from '@syndiqia/platform/permissions';
import { useApi } from './api';
import { useRegistryMutation } from './queries';
import { useSyndicate } from './session';

export function useRequests(status: 'open' | 'all') {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'comms', 'requests', status],
    queryFn: () => api<RequestSummary[]>(`${s.api}/comms/requests?status=${status}`),
    enabled: s.can('requests.create'),
  });
}

export function useRequest(id: string, enabled = true) {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({ queryKey: [s.tenantId, 'comms', 'request', id], queryFn: () => api<RequestDetail>(`${s.api}/comms/requests/${id}`), enabled });
}

export function useDeliveries() {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'comms', 'deliveries'],
    queryFn: () => api<Delivery[]>(`${s.api}/comms/deliveries`),
    enabled: s.can('comms.log.read'),
    refetchInterval: 10_000, // statuses change as the worker sends
  });
}

export interface Member {
  userId: string;
  name: string;
  email: string | null;
  roles: Role[];
}

export function useMembers() {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({ queryKey: [s.tenantId, 'members'], queryFn: () => api<Member[]>(`${s.api}/members`) });
}

/** The caller's units: owned and occupied by default (to book, report a problem), or owned only (their account). */
export function useMyUnits(scope: 'used' | 'owned' = 'used') {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({ queryKey: [s.tenantId, 'my-units', scope], queryFn: () => api<string[]>(`${s.api}/registry/my-units?scope=${scope}`) });
}

/** A write to the Comms API; refreshes requests and the email log. */
export function useCommsMutation<Input, Output>(request: (api: ReturnType<typeof useApi>, base: string, input: Input) => Promise<Output>) {
  return useRegistryMutation<Input, Output>((api, registryBase, input) => request(api, registryBase.replace(/\/registry$/, '/comms'), input), ['comms']);
}
